import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Toggle from "../../Shared/Toggle";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import {
  useBookingPage,
  useUpdateSettingsTab,
} from "../../../hooks/useBookingPage";

const BookingApprovalForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Fetch config data
  const { data: configData, isLoading } = useBookingPage();

  // Read the hide-address flag to enforce mutual exclusion in the UI
  const hideFixedAddressUntilConfirmed =
    configData?.data?.hideFixedAddressUntilConfirmed ?? false;

  // Update mutation for settings tab
  const { mutate: updateSettings, isPending: isUpdating } =
    useUpdateSettingsTab();

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      autoApproval: true,
      allowOutsideAvailability: false,
    },
  });

  // Watch form values
  const autoApproval = watch("autoApproval");
  const allowOutsideAvailability = watch("allowOutsideAvailability");

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      const formData = {
        autoApproval: configData.data.autoApproval ?? true,
        allowOutsideAvailability:
          configData.data.allowOutsideAvailability ?? false,
      };

      // Reset form and mark as pristine (not dirty)
      reset(formData, { keepDirty: false });
    }
  }, [configData, reset]);

  // Toggle change handlers
  const handleAutoApprovalToggle = (checked) => {
    setValue("autoApproval", checked, { shouldDirty: true });
    setErrors({});
    setSuccess(false);
  };

  const handleOutsideAvailabilityToggle = (checked) => {
    setValue("allowOutsideAvailability", checked, { shouldDirty: true });
    setErrors({});
    setSuccess(false);
  };

  const onSubmit = async (data) => {
    await performSave(data);
  };

  const performSave = async (data) => {
    setErrors({});
    setSuccess(false);

    updateSettings(data, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage =
          errorMessage || "Failed to save booking approval settings";

        // Handle specific validation errors
        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({
          title: "Error",
          message: displayMessage,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showToggle={true} inputCount={0} />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-ink-900">Auto-approval</h3>
            <p className="text-sm text-ink-500">
              Automatically approve and confirm new bookings without requiring
              your manual review
            </p>
            {hideFixedAddressUntilConfirmed && (
              <p className="mt-1 text-sm text-signal-amber-600">
                Auto-approval is off while Location Address Privacy is on —
                approving each booking is what reveals your address. Turn off
                address privacy to auto-confirm bookings.
              </p>
            )}
          </div>
          <Toggle
            checked={autoApproval}
            onChange={handleAutoApprovalToggle}
            disabled={hideFixedAddressUntilConfirmed}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-ink-900">
              Allow outside availability
            </h3>
            <p className="text-sm text-ink-500">
              Allow customers to book outside your set business hours and
              coverage areas
            </p>
          </div>
          <Toggle
            checked={allowOutsideAvailability}
            onChange={handleOutsideAvailabilityToggle}
          />
        </div>
      </div>
      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {/* Alerts */}
      {errors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={errors.title} message={errors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen
            title="Success"
            message="Booking approval settings saved successfully"
          />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default BookingApprovalForm;
